import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, TrendingUp, FileText } from "lucide-react";
import api, { fmtMXNFull } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Expenses from "@/pages/Expenses";
import ProjectTabs from "@/components/ProjectTabs";

/**
 * Página de gastos integrada a un proyecto específico.
 *
 * Renderiza el módulo Expenses en modo `lockedProjectId`, lo que:
 *   - Oculta el filtro de proyecto
 *   - Pre-selecciona y bloquea el proyecto en el modal de captura
 *   - Filtra automáticamente solo gastos de este proyecto
 *   - Oculta el tab "Administrativos"
 *
 * Muestra arriba un mini-header con el código/nombre del proyecto
 * y un botón para volver al detalle del proyecto.
 */
export default function ProjectExpenses() {
  const { id } = useParams();
  const { user } = useAuth();
  const canSeeReport = ["admin", "contador"].includes(user?.role);
  const [project, setProject] = useState(null);

  useEffect(() => {
    let m = true;
    api.get(`/projects/${id}`).then((r) => { if (m) setProject(r.data); }).catch(() => {});
    return () => { m = false; };
  }, [id]);

  return (
    <div data-testid="project-expenses-page">
      {/* Mini-breadcrumb del proyecto */}
      {project && (
        <div className="px-6 lg:px-8 pt-4">
          <ProjectTabs projectId={id} active="expenses" />
        </div>
      )}
      {project && (
        <div
          className="px-6 lg:px-8 py-3 border-b flex items-center justify-between gap-3 flex-wrap"
          style={{ borderColor: "var(--border)", background: "rgba(15,23,42,0.55)" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={canSeeReport ? `/projects/${id}/report` : `/projects/${id}`}
              data-testid="back-to-project"
              className="btn-ghost px-3 py-1.5 rounded-sm flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold shrink-0"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Volver al proyecto
            </Link>
            <div className="min-w-0">
              <div className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>// PROYECTO</div>
              <div className="font-display font-bold text-sm text-slate-100 truncate">
                {project.name}
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                <span className="font-mono">{project.code}</span>{project.client ? ` · Cliente: ${project.client}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {canSeeReport && (
              <Link
                to={`/projects/${id}/report`}
                className="btn-ghost px-2.5 py-1.5 rounded-sm flex items-center gap-1.5 text-[10px] uppercase tracking-widest"
              >
                <FileText className="w-3 h-3" /> Reporte
              </Link>
            )}
            <Link
              to={`/projects/${id}/budget`}
              className="btn-ghost px-2.5 py-1.5 rounded-sm flex items-center gap-1.5 text-[10px] uppercase tracking-widest"
            >
              <TrendingUp className="w-3 h-3" /> Presupuesto
            </Link>
            <div className="text-right">
              <div className="label-mono text-[10px]">Presupuesto</div>
              <div className="font-mono font-bold text-sm text-slate-100 tabular-nums">{fmtMXNFull(project.budget || 0)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Módulo de gastos limitado a este proyecto */}
      <Expenses lockedProjectId={id} />
    </div>
  );
}
